import {
	IDrawerDataListColumn,
	PaginatedDrawerAutoDataList,
	Tag,
	laravelPaginatedListFetchConfig,
	useHasPermission,
	useLaravelCrudEntity,
} from "@dgs/core";
import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { PermissionName } from "../../../shared/types/permission";
import { ContactTypeCreateDrawer, ContactTypeUpdateDrawer } from "../contactType/ContactTypeDrawer";
import { ContactTypeListContextMenu } from "../contactType/ContactTypeListContextMenu";
import { contactService } from "../shared/api/contactService";
import { contactTypeService } from "../shared/api/contactType";
import { ContactTypeIndexResource } from "../shared/types/contactType";

const Chip = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: ${({ theme }) => theme.spacing(2)};
	align-items: center;
	justify-content: flex-end;
`;

export const ContactTypeList: FC = () => {
	const { t } = useTranslation();

	const crudOperations = useLaravelCrudEntity(
		contactTypeService,
		{
			entityName: t("Contact type"),
			messages: {
				create: t("Contact type was saved."),
				update: t("Contact type was saved."),
				delete: t("Contact type was deleted."),
			},
		},
		[contactService.indexKey],
	);

	const dataListColumns: IDrawerDataListColumn<ContactTypeIndexResource>[] = useMemo(
		() => [
			{
				heading: t("Name"),
				valueKey: "name",
				type: "value",
				sortable: true,
				render: (type) => (
					<Chip>
						{type.name}
						{type.isDefault && (
							<Tag size="small" color="primary" name="isDefault">
								{t("Default")}
							</Tag>
						)}
					</Chip>
				),
			},
			{
				heading: "",
				type: "actions",
				size: "max-content",
				action: ({ id }, openDrawer) => (
					<ContactTypeListContextMenu
						contactTypeId={id}
						handleEdit={openDrawer}
						handleDelete={() => crudOperations.deleteEntity(id)}
					/>
				),
			},
		],
		[crudOperations, t],
	);
	const canCreate = useHasPermission<PermissionName>(["contactType.create"]);
	const canUpdate = useHasPermission<PermissionName>(["contactType.update"]);

	return (
		<PaginatedDrawerAutoDataList
			canUpdate={canUpdate}
			canCreate={canCreate}
			initialSearch={{ search: "", searchFields: [{ name: "name", label: t("Name") }] }}
			translations={{
				title: t("Contact types"),
				empty: t("No contact types available"),
			}}
			renderCreateDrawer={(drawerProps) => (
				<ContactTypeCreateDrawer
					{...drawerProps}
					onSubmit={(entityFormState) => crudOperations.createEntity(entityFormState)}
				/>
			)}
			renderUpdateDrawer={(drawerProps) => (
				<ContactTypeUpdateDrawer
					{...drawerProps}
					onSubmit={(entityFormState) => crudOperations.updateEntity(drawerProps.id, entityFormState)}
				/>
			)}
			columns={dataListColumns}
			listFetchConfig={laravelPaginatedListFetchConfig(contactTypeService.listKey, contactTypeService.index)}
		/>
	);
};
